import { api } from '@/lib/axios';

export interface TutorialDownloadFile {
  fileName: string;
  url: string;
}

export interface TutorialSlide {
  code: string;
  title: string;
  description: string | null;
  displayOrder: number;
  section: string | null;
  assetBaseUrl: string;
  contentKind: string;
  htmlContent: string | null;
  imageUrl: string | null;
  renderedDescription: string | null;
}

export interface TutorialDownload {
  code: string;
  title: string;
  description: string | null;
  section: string | null;
  assetBaseUrl: string;
  files: TutorialDownloadFile[];
  primaryDownloadUrl: string | null;
}

export interface TutorialFaq {
  code: string;
  title: string;
  description: string | null;
  displayOrder: number;
  section: string | null;
  assetBaseUrl: string;
  renderedDescription: string | null;
}

export interface TutorialVisibility {
  hasActiveFAQ: boolean;
  hasActiveTutorialContent: boolean;
}

const previewParams = (previewToken?: string) =>
  previewToken ? { previewToken } : undefined;

export const tutorialService = {
  getVisibility: (siteGroupCode: string) =>
    api.get<TutorialVisibility>('/Tutorials/Visibility', {
      params: { siteGroupCode },
    }).then((r) => r.data),

  getQuickTutorials: (siteGroupCode?: string, previewToken?: string) =>
    api.get<TutorialSlide[]>('/Tutorials/Quick', {
      params: { siteGroupCode, ...previewParams(previewToken) },
    }).then((r) => r.data),

  getDownloads: (siteGroupCode?: string, previewToken?: string) =>
    api.get<TutorialDownload[]>('/Tutorials/Downloads', {
      params: { siteGroupCode, ...previewParams(previewToken) },
    }).then((r) => r.data),

  getFaqs: (siteGroupCode?: string, previewToken?: string) =>
    api.get<TutorialFaq[]>('/Tutorials/FAQ', {
      params: { siteGroupCode, ...previewParams(previewToken) },
    }).then((r) => r.data),
};

/** Replace {filename} / !filename! placeholders with rendered HTML links/images. */
export function renderTutorialHtml(description: string | null, assetBaseUrl: string): string {
  if (!description) return '';
  const base = assetBaseUrl.endsWith('/') ? assetBaseUrl : `${assetBaseUrl}/`;
  return description.replace(/\{([^}]+)\}|!([^!]+)!/g, (_, f1, f2) => {
    const file = f1 ?? f2;
    const url  = `${base}${encodeURIComponent(file)}`;
    const ext  = (file.split('.').pop() ?? '').toLowerCase();
    return ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'svg'].includes(ext)
      ? `<img src="${url}" alt="${file}" style="max-width:100%;border-radius:4px;" />`
      : `<a href="${url}" target="_blank" rel="noopener noreferrer">${file}</a>`;
  });
}
